import { ProductGender } from "src/types/graphql.d";
import { FC, memo, useEffect } from "react";
import useCartCarousels from "src/hooks/useCartCarousels";
import { InView } from "react-intersection-observer";
import { ContentMobileFullscreen } from "src/atoms/Content";
import {
  CardsLoader,
  StyledCarousels,
} from "src/organisms/ProductView/ProductView.styled";
import { LoadingSpinner } from "src/atoms/LoadingSpinner";
import { ProductCarousel } from "src/molecules/ProductCarousel";
import { useFeatureFlags } from "src/hooks/useFeatureFlags";
import { GATED_CART_PAGE } from "src/constants/gatedItem";
import { useIsLoggedIn } from "src/hooks/useIsLoggedIn";

const Carousel = memo(ProductCarousel);

interface CartViewCarouselsProps {
  shopType: ProductGender;
  limit?: number;
}

const CartViewCarousels: FC<CartViewCarouselsProps> = ({ shopType, limit }) => {
  const { data, loading, error } = useCartCarousels({ shopType, limit });
  const { enableWishListCarouselOnCart, enableGatedHomepage } =
    useFeatureFlags();
  const isLoggedIn = useIsLoggedIn();

  const isGated = enableGatedHomepage && !isLoggedIn;

  useEffect(() => {
    if (error) {
      const errors = error.graphQLErrors
        .map((graphQLError) => graphQLError.message)
        .join(", ");
      console.error(errors);
    }
  }, [error]);

  const cartCarousels = enableWishListCarouselOnCart
    ? data?.cartCarousels?.carousels
    : data?.cartCarousels?.carousels?.filter(
        (carousel) => carousel.id !== "cart_favorite_items_carousel"
      );

  return (
    <InView rootMargin="150px 0px 0px 0px" triggerOnce={true}>
      {({ inView, ref }) => (
        <ContentMobileFullscreen ref={ref}>
          {loading ? (
            <CardsLoader>
              <LoadingSpinner />
            </CardsLoader>
          ) : (
            cartCarousels?.map(
              (carousel) =>
                inView && (
                  <StyledCarousels key={carousel.id} data-testid={carousel.id}>
                    <Carousel
                      genderId={shopType}
                      title={carousel.title}
                      products={carousel.products}
                      titleForTracking={carousel.title}
                      shouldReset={true}
                      slidesDesktop={6}
                      slidesMobile={2.5}
                      slidesTablet={6}
                      zoomImageOnHover={true}
                      allowAddToCart={carousel.allow_add_to_cart && !isGated}
                      responseSourceType={carousel.response_source_type}
                      trackingData={{
                        shopType,
                        pageName: GATED_CART_PAGE,
                        carouselType: carousel.id,
                      }}
                      isPDPCarousel
                    />
                  </StyledCarousels>
                )
            )
          )}
        </ContentMobileFullscreen>
      )}
    </InView>
  );
};

export default CartViewCarousels;
